import type { VinInspectionItem, InspectionDamage } from './inspectionTypes';
import { isDamageDeleted } from './utils/damageAnnotationUtils';

type InspectionRecord = Partial<VinInspectionItem> & {
  VIN_DETAIL_ID?: string | number;
  vinDetailId?: string | number;
};

export const getInspectionRecordId = (record?: InspectionRecord | null): string | undefined => {
  if (!record) return undefined;

  const raw = record.inspectionId ?? record.VIN_DETAIL_ID ?? record.vinDetailId;
  if (raw === undefined || raw === null || raw === '') return undefined;

  return String(raw);
};

export const getInspectionSelection = (record?: InspectionRecord | null): {
  inspectionId?: string;
  inspectionDateTime?: string;
} => {
  if (!record) {
    return {};
  }

  const inspectionId = getInspectionRecordId(record);
  const inspectionDateTime = record.dateTime ? String(record.dateTime) : undefined;

  return {
    inspectionId,
    inspectionDateTime,
  };
};

export const getInspectionPanelKey = (item: InspectionRecord, index = 0): string => {
  return getInspectionRecordId(item) ?? String(index);
};

export const findInspectionPanelKey = (
  items: VinInspectionItem[],
  options?: { inspectionId?: string; dateTime?: string }
): string | undefined => {
  if (!items.length) return undefined;

  const targetInspectionId = options?.inspectionId ? String(options.inspectionId) : undefined;

  if (targetInspectionId) {
    const matchById = items.find((item, index) => getInspectionPanelKey(item, index) === targetInspectionId);
    if (matchById) {
      return getInspectionPanelKey(matchById, items.indexOf(matchById));
    }
  }

  if (options?.dateTime) {
    const matchByDate = items.find(item => item.dateTime === options.dateTime);
    if (matchByDate) {
      return getInspectionPanelKey(matchByDate, items.indexOf(matchByDate));
    }
  }

  return getInspectionPanelKey(items[0], 0);
};

/** Active (non-deleted) unique damage count for list badges and sidebar totals. */
export const getUniqueDamageCount = (
  damages?: Array<Pick<InspectionDamage, 'id' | 'isDeleted' | 'deleted' | 'status'> & Record<string, unknown>> | null
): number => {
  if (!Array.isArray(damages) || damages.length === 0) return 0;

  const ids = new Set<string | number>();

  damages.forEach(damage => {
    if (isDamageDeleted(damage)) {
      return;
    }

    const id = damage?.id;
    if (id !== undefined && id !== null && id !== '') {
      ids.add(id);
    }
  });

  return ids.size;
};

/**
 * Prefer counting from the damages array (excluding soft-deletes).
 * Only fall back to server `damageCount` when no damages array is present —
 * otherwise `0 || damageCount` would resurrect deleted rows into the badge.
 */
export const getInspectionActiveDamageCount = (
  inspection?: Pick<VinInspectionItem, 'damages' | 'damageCount'> | null,
  fetchedActiveDamages?: InspectionDamage[] | null
): number => {
  if (Array.isArray(fetchedActiveDamages)) {
    return getUniqueDamageCount(fetchedActiveDamages);
  }

  if (inspection && Array.isArray(inspection.damages)) {
    return getUniqueDamageCount(inspection.damages);
  }

  return Number(inspection?.damageCount || 0);
};
